import React, { useState, useEffect } from 'react';
import {
    Plus, FileText, Upload, Trash2, X, Calculator, Wallet, Save,
    Eye, Tag, Edit3, MessageSquare, Loader2, History, Printer, CheckCircle,
    Download
} from 'lucide-react';
import api from '../api/axiosInstance';

const ClientBilling1 = () => {
    const [activeModal, setActiveModal] = useState(null);
    const [invoiceType, setInvoiceType] = useState('flat');
    const [clients, setClients] = useState([]);
    const [projects, setProjects] = useState([]);
    const [invoices, setInvoices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saveLoading, setSaveLoading] = useState(false);
    const [downloadingId, setDownloadingId] = useState(null);
    const [selectedInvoice, setSelectedInvoice] = useState(null);
    const [invoiceHistory, setInvoiceHistory] = useState([]);
    const [loadingHistory, setLoadingHistory] = useState(false);

    const [editingInvoiceId, setEditingInvoiceId] = useState(null);
    const [bill, setBill] = useState({
        client_id: '', project_id: '', invoice_no: '', invoice_date: new Date().toISOString().split('T')[0],
        taxable_amount: 0, gst_rate: 18, cgst: 0, sgst: 0, total: 0,
        items: [{ id: Date.now(), desc: '', qty: 1, rate: 0 }]
    });

    const [payForm, setPayForm] = useState({
        id: null, invoice_id: '', amount: '', tds: 0, date: new Date().toISOString().split('T')[0],
        mode: 'NEFT', reference_no: '', notes: '', proof: null
    });
    // Inside ClientBilling component
    const [toast, setToast] = useState(null);

    const showToast = (msg, type = 'success') => {
        setToast({ msg, type });
        setTimeout(() => setToast(null), 3000);
    };
    useEffect(() => { loadData(); }, []);

    const loadData = async () => {
        try {
            const [clientRes, projectRes, invoiceRes] = await Promise.all([
                api.get('get_directory.php?type=client'),
                api.get('get_projects.php'),
                api.get('get_client_invoices.php')
            ]);
            setClients(clientRes.data.data || []);
            setProjects(projectRes.data.projects || []);
            setInvoices(invoiceRes.data.data || []);
        } catch (err) { console.error(err); }
        finally { setLoading(false); }
    };

    // Calculation logic with safety checks
    useEffect(() => {
        let subtotal = 0;
        if (invoiceType === 'itemized') {
            subtotal = bill.items.reduce((sum, i) => sum + (parseFloat(i.qty || 0) * parseFloat(i.rate || 0)), 0);
        } else {
            subtotal = parseFloat(bill.taxable_amount || 0);
        }
        const totalGstAmt = (subtotal * parseFloat(bill.gst_rate || 0)) / 100;
        setBill(prev => ({
            ...prev,
            taxable_amount: subtotal,
            cgst: totalGstAmt / 2,
            sgst: totalGstAmt / 2,
            total: subtotal + totalGstAmt
        }));
    }, [bill.taxable_amount, bill.gst_rate, bill.items, invoiceType]);

    const openEditInvoice = (e, inv) => {
        e.stopPropagation();
        setEditingInvoiceId(inv.id);
        setInvoiceType(inv.invoice_type ? inv.invoice_type.toLowerCase() : 'flat');

        // CRITICAL FIX: Map database fields to the state names used for calculations
        setBill({
            ...inv,
            taxable_amount: parseFloat(inv.taxable_amount || 0),
            gst_rate: parseInt(inv.gst_rate || 18),
            items: inv.billing_items_json ? JSON.parse(inv.billing_items_json) : [{ id: Date.now(), desc: '', qty: 1, rate: 0 }]
        });
        setActiveModal('quick_save');
    };

    const handleFormSubmit = async (e) => {
        e.preventDefault();
        setSaveLoading(true);
        const endpoint = editingInvoiceId ? 'update_client_invoice.php' : 'save_and_generate_invoice.php';
        const payload = {
            ...bill,
            id: editingInvoiceId,
            invoice_type: invoiceType,
            cgst_amount: bill.cgst,
            sgst_amount: bill.sgst,
            total_amount: bill.total,
            billing_items_json: invoiceType === 'itemized' ? JSON.stringify(bill.items) : null
        };

        try {
            const res = await api.post(endpoint, payload);
            console.log(res.data);
            if (res.data.status === 'success') {
                if (activeModal === 'generate_pdf') await downloadPDF(res.data.invoice_id || editingInvoiceId, bill.invoice_no);
                setActiveModal(null);
                setEditingInvoiceId(null);
                loadData();
            }
        } catch (err) { alert("Save failed"); }
        finally { setSaveLoading(false); }
    };

    const downloadPDF = async (invId, invNo) => {
        setDownloadingId(invId);
        try {
            const response = await api.get(`view_invoice_pdf.php?invoice_id=${invId}`, { responseType: 'blob' });
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `Invoice_${invNo.replace(/\//g, '_')}.pdf`);
            document.body.appendChild(link);
            link.click();
            link.remove();

            showToast("Invoice Downloaded Successfully!"); // Trigger notification
        } catch (err) {
            showToast("Download Failed", "error");
        } finally {
            setDownloadingId(null);
        }
    };

    const viewInvoiceDetails = async (inv) => {
        setSelectedInvoice(inv);
        setLoadingHistory(true);
        try {
            const res = await api.get(`get_invoice_history.php?invoice_id=${inv.id}`);
            setInvoiceHistory(res.data.payments || []);
        } catch (err) { console.error(err); }
        finally { setLoadingHistory(false); }
    };

    const handleRecordPayment = async (e) => {
        e.preventDefault();
        const formData = new FormData();
        Object.keys(payForm).forEach(key => { if (payForm[key] !== null) formData.append(key, payForm[key]); });
        const endpoint = payForm.id ? 'update_client_payment.php' : 'record_client_payment.php';
        try {
            const res = await api.post(endpoint, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
            if (res.data.status === 'success') {
                setActiveModal(null);
                loadData();
                if (selectedInvoice) viewInvoiceDetails(selectedInvoice);
            }
        } catch (err) { alert("Payment error"); }
    };

    const deletePayment = async (payId) => {
        if (!window.confirm("Delete this payment?")) return;
        try {
            await api.post('delete_client_payment.php', { id: payId });
            viewInvoiceDetails(selectedInvoice);
            loadData();
        } catch (err) { alert("Delete failed"); }
    };

    const resetBillForm = () => {
        setEditingInvoiceId(null);
        setBill({
            client_id: '', project_id: '',
            invoice_no: `LI/${new Date().getFullYear()}/${Math.floor(1000 + Math.random() * 9000)}`,
            invoice_date: new Date().toISOString().split('T')[0],
            taxable_amount: 0, gst_rate: 18, cgst: 0, sgst: 0, total: 0,
            items: [{ id: Date.now(), desc: '', qty: 1, rate: 0 }]
        });
    };

    if (loading) return <div className="p-20 text-center font-black animate-pulse text-slate-300">Syncing Ledger...</div>;

    return (
        <div className="space-y-8 relative p-4">
            <div className="flex justify-between items-center">
                <h1 className="text-4xl font-black text-slate-900 italic uppercase">Billing Center</h1>
                <button onClick={() => { resetBillForm(); setActiveModal('generate_pdf'); }} className="bg-slate-900 text-white px-8 py-4 rounded-2xl font-black shadow-xl flex items-center gap-2 hover:bg-indigo-600 transition-all text-[10px] uppercase">
                    <Printer size={18} /> New Tax Invoice
                </button>
            </div>

            {/* Main Table */}
            <div className="bg-white rounded-[40px] border border-slate-100 shadow-sm overflow-hidden">
                <table className="w-full text-left border-collapse">
                    <thead className="bg-slate-50/50 text-[10px] font-black text-slate-400 uppercase tracking-widest border-b border-slate-100">
                        <tr>
                            <th className="p-6">Invoice Details</th>
                            <th className="p-6">Site Info</th>
                            <th className="p-6">Bill Total</th>
                            <th className="p-6 text-rose-500">Balance</th>
                            <th className="p-6 text-center">PDF</th>
                            <th className="p-6 text-right">Action</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-50 font-bold text-sm">
                        {invoices.map((inv) => (
                            <tr key={inv.id} className="hover:bg-indigo-50/50 transition-all cursor-pointer group" onClick={() => viewInvoiceDetails(inv)}>
                                <td className="p-6">
                                    <p className="font-black text-slate-900 group-hover:text-indigo-600">{inv.invoice_no}</p>
                                    <p className="text-[10px] text-slate-400 font-black">{inv.invoice_date}</p>
                                </td>
                                <td className="p-6 uppercase text-xs">
                                    <p className="font-black text-slate-700">{inv.project_name}</p>
                                    <p className="text-[10px] text-slate-400">{inv.client_name}</p>
                                </td>
                                <td className="p-6 font-black text-slate-900">₹{(parseFloat(inv.total_amount) || 0).toLocaleString()}</td>
                                <td className="p-6 font-black text-rose-500 italic">₹{((parseFloat(inv.total_amount) || 0) - (parseFloat(inv.paid_amount) || 0)).toLocaleString()}</td>
                                <td className="p-6 text-center" onClick={(e) => e.stopPropagation()}>
                                    <button
                                        disabled={downloadingId === inv.id}
                                        onClick={() => downloadPDF(inv.id, inv.invoice_no)}
                                        className={`p-3 rounded-xl transition-all shadow-sm ${downloadingId === inv.id
                                            ? 'bg-slate-50 text-indigo-500'
                                            : 'bg-slate-100 text-slate-400 hover:bg-indigo-600 hover:text-white'
                                            }`}
                                        title="Download Tax Invoice"
                                    >
                                        {downloadingId === inv.id ? (
                                            <Loader2 size={16} className="animate-spin" />
                                        ) : (
                                            <Download size={16} />
                                        )}
                                    </button>
                                </td>
                                <td className="p-6 text-right" onClick={(e) => e.stopPropagation()}>
                                    <div className="flex justify-end gap-2">
                                        <button onClick={(e) => openEditInvoice(e, inv)} className="p-2 text-slate-400 hover:text-indigo-600" title="Edit"><Edit3 size={18} /></button>
                                        <button onClick={() => { setPayForm({ id: null, invoice_id: inv.id, amount: '', tds: 0, date: new Date().toISOString().split('T')[0], mode: 'NEFT', reference_no: '', notes: '', proof: null }); setActiveModal('record_payment'); }} className="px-5 py-2.5 bg-slate-900 text-white rounded-xl text-[10px] font-black uppercase hover:bg-emerald-600 transition-all shadow-md">Add Payment</button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* UNIFIED MODAL (Add / Edit) */}
            {(activeModal === 'quick_save' || activeModal === 'generate_pdf') && (
                <div className="fixed inset-0 z-[200] flex items-center justify-center p-6 bg-slate-900/60 backdrop-blur-md">
                    <form onSubmit={handleFormSubmit} className="bg-white w-full max-w-6xl rounded-[48px] shadow-2xl p-12 space-y-8 overflow-y-auto max-h-[90vh]">
                        <div className="flex justify-between items-center">
                            <h2 className="text-3xl font-black italic tracking-tighter uppercase">{editingInvoiceId ? 'Edit Invoice Details' : 'New Invoice Generator'}</h2>
                            <button type="button" onClick={() => { setActiveModal(null); setEditingInvoiceId(null); }} className="p-2 bg-slate-100 rounded-full hover:bg-rose-50 text-slate-400 transition-all"><X /></button>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            <div className="space-y-2">
                                <label className="text-[10px] font-black text-slate-400 uppercase ml-2">Client</label>
                                <select required disabled={!!editingInvoiceId} className="w-full p-4 bg-slate-50 rounded-2xl font-bold outline-none" value={bill.client_id} onChange={e => setBill({ ...bill, client_id: e.target.value })}>
                                    <option value="">Choose Client</option>
                                    {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                                </select>
                            </div>
                            <div className="space-y-2">
                                <label className="text-[10px] font-black text-slate-400 uppercase ml-2">Project</label>
                                <select required disabled={!!editingInvoiceId} className="w-full p-4 bg-slate-50 rounded-2xl font-bold outline-none" value={bill.project_id} onChange={e => setBill({ ...bill, project_id: e.target.value })}>
                                    <option value="">Link Site</option>
                                    {projects.filter(p => p.client_id == bill.client_id).map(p => <option key={p.id} value={p.id}>{p.project_name}</option>)}
                                </select>
                            </div>
                            <div className="space-y-2">
                                <label className="text-[10px] font-black text-slate-400 uppercase ml-2">Invoice Meta (Editable)</label>
                                <div className="grid grid-cols-2 gap-2">
                                    <input placeholder="Inv No" className="p-4 bg-slate-50 border-2 border-indigo-50 rounded-2xl font-black text-indigo-600 outline-none" value={bill.invoice_no} onChange={e => setBill({ ...bill, invoice_no: e.target.value })} />
                                    <input type="date" className="p-4 bg-slate-50 rounded-2xl font-bold outline-none" value={bill.invoice_date} onChange={e => setBill({ ...bill, invoice_date: e.target.value })} />
                                </div>
                            </div>
                        </div>

                        <div className="flex gap-4 p-2 bg-slate-100 rounded-2xl w-max">
                            <button type="button" onClick={() => setInvoiceType('flat')} className={`px-6 py-2 rounded-xl text-[10px] font-black uppercase transition-all ${invoiceType === 'flat' ? 'bg-white text-indigo-600 shadow-sm' : 'text-slate-400'}`}>Flat Amount</button>
                            <button type="button" onClick={() => setInvoiceType('itemized')} className={`px-6 py-2 rounded-xl text-[10px] font-black uppercase transition-all ${invoiceType === 'itemized' ? 'bg-white text-indigo-600 shadow-sm' : 'text-slate-400'}`}>Itemized List</button>
                        </div>

                        {invoiceType === 'flat' ? (
                            <div className="p-8 bg-slate-50 rounded-3xl space-y-4">
                                <label className="text-[10px] font-black text-slate-400 uppercase">Taxable Sub-Total</label>
                                <input type="number" placeholder="0.00" className="w-full p-6 bg-white rounded-2xl text-3xl font-black text-indigo-600 outline-none" value={bill.taxable_amount} onChange={e => setBill({ ...bill, taxable_amount: e.target.value })} />
                            </div>
                        ) : (
                            <div className="space-y-4">
                                {bill.items.map((item, idx) => (
                                    <div key={item.id} className="grid grid-cols-12 gap-4 items-end bg-slate-50 p-4 rounded-3xl">
                                        <div className="col-span-6"><input placeholder="Item Description" className="w-full p-4 bg-white rounded-2xl font-bold text-sm" value={item.desc} onChange={e => { const n = [...bill.items]; n[idx].desc = e.target.value; setBill({ ...bill, items: n }); }} /></div>
                                        <div className="col-span-2"><input type="number" placeholder="Qty" className="w-full p-4 bg-white rounded-2xl font-bold text-sm" value={item.qty} onChange={e => { const n = [...bill.items]; n[idx].qty = e.target.value; setBill({ ...bill, items: n }); }} /></div>
                                        <div className="col-span-3"><input type="number" placeholder="Rate" className="w-full p-4 bg-white rounded-2xl font-bold text-sm" value={item.rate} onChange={e => { const n = [...bill.items]; n[idx].rate = e.target.value; setBill({ ...bill, items: n }); }} /></div>
                                        <div className="col-span-1 py-4 text-center"><button type="button" onClick={() => setBill({ ...bill, items: bill.items.filter(i => i.id !== item.id) })} className="text-rose-500"><Trash2 size={20} /></button></div>
                                    </div>
                                ))}
                                <button type="button" onClick={() => setBill({ ...bill, items: [...bill.items, { id: Date.now(), desc: '', qty: 1, rate: 0 }] })} className="text-[10px] font-black text-indigo-600 uppercase flex items-center gap-2 p-4 border-2 border-dashed border-indigo-100 rounded-3xl w-full justify-center hover:bg-indigo-50">Add Description Line</button>
                            </div>
                        )}

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-10 border-t pt-8">
                            <div className="space-y-4">
                                <div className="flex justify-between items-center bg-slate-50 p-3 rounded-xl">
                                    <span className="text-[10px] font-black text-slate-400 uppercase">GST Rate</span>
                                    <select className="bg-transparent font-bold outline-none" value={bill.gst_rate} onChange={e => setBill({ ...bill, gst_rate: e.target.value })}>
                                        <option value="18">18% Standard</option><option value="12">12% Service</option><option value="5">5% Contract</option><option value="0">Exempt</option>
                                    </select>
                                </div>
                                <div className="flex justify-between text-xs font-bold text-slate-500 uppercase px-2"><span>CGST Split</span><span>₹{(bill.cgst || 0).toLocaleString()}</span></div>
                                <div className="flex justify-between text-xs font-bold text-slate-500 uppercase px-2"><span>SGST Split</span><span>₹{(bill.sgst || 0).toLocaleString()}</span></div>
                            </div>
                            <div className="text-right space-y-2">
                                <p className="text-[10px] font-black text-slate-400 uppercase">Total Invoice Value</p>
                                <h3 className="text-5xl font-black text-slate-900 italic tracking-tighter">₹{(bill.total || 0).toLocaleString()}</h3>
                            </div>
                        </div>

                        <button
                            disabled={saveLoading}
                            className={`w-full py-6 rounded-[32px] font-black text-xl shadow-2xl flex items-center justify-center gap-3 transition-all ${activeModal === 'generate_pdf' ? 'bg-indigo-600 text-white hover:bg-indigo-700' : 'bg-slate-900 text-white hover:bg-slate-800'
                                }`}
                        >
                            {saveLoading ? (
                                <Loader2 className="animate-spin" />
                            ) : (
                                activeModal === 'generate_pdf' ? <Printer size={24} /> : <Save size={24} />
                            )}

                            {saveLoading
                                ? "Generating PDF..."
                                : editingInvoiceId
                                    ? 'Update Invoice Record'
                                    : (activeModal === 'generate_pdf' ? 'Generate & Download PDF' : 'Save Invoice Record')
                            }
                        </button>
                    </form>
                </div>
            )}

            {/* SIDEBAR */}
            {selectedInvoice && (
                <div className="fixed inset-0 z-[120] flex items-center justify-end bg-slate-900/40 backdrop-blur-sm">
                    <div className="bg-white h-full w-full max-w-xl shadow-2xl p-10 overflow-y-auto animate-in slide-in-from-right duration-300">
                        <div className="flex justify-between items-center mb-10 border-b border-slate-50 pb-6">
                            <div><h2 className="text-2xl font-black text-slate-900 uppercase italic leading-none">{selectedInvoice.invoice_no}</h2><p className="text-[10px] font-black text-slate-400 uppercase mt-1">{selectedInvoice.project_name}</p></div>
                            <button onClick={() => setSelectedInvoice(null)} className="p-3 bg-slate-100 rounded-2xl hover:text-rose-500 transition-all"><X /></button>
                        </div>

                        <div className="p-8 bg-slate-900 rounded-[40px] text-white shadow-xl mb-10">
                            <div className="flex justify-between text-3xl font-black italic">
                                <span>Balance</span>
                                <span className="text-rose-400">₹{((parseFloat(selectedInvoice.total_amount) || 0) - (parseFloat(selectedInvoice.paid_amount) || 0)).toLocaleString()}</span>
                            </div>
                            <div className="mt-6 pt-6 border-t border-white/10 flex justify-between text-xs font-bold opacity-60">
                                <span>Total: ₹{(parseFloat(selectedInvoice.total_amount) || 0).toLocaleString()}</span>
                                <span>Received: ₹{(parseFloat(selectedInvoice.paid_amount) || 0).toLocaleString()}</span>
                            </div>
                        </div>

                        <h3 className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-6 flex items-center gap-2"><History size={14} /> Payment Timeline</h3>
                        <div className="space-y-4">
                            {invoiceHistory.length === 0 ? <p className="text-center text-slate-300 italic font-bold">No payments found</p> :
                                invoiceHistory.map((pay) => (
                                    <div key={pay.id} className="p-5 bg-slate-50 rounded-3xl flex justify-between items-center group">
                                        <div>
                                            <p className="font-black text-slate-900">₹{(parseFloat(pay.received_amount) || 0).toLocaleString()}</p>
                                            <p className="text-[10px] font-bold text-slate-400 uppercase">{pay.payment_date} • {pay.payment_mode}</p>
                                        </div>
                                        <div className="flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                            {pay.receipt_copy_path && <a href={`https://backendibos.laxmaninterior.in/${pay.receipt_copy_path}`} target="_blank" rel="noreferrer" className="p-2 bg-white rounded-lg text-indigo-500 shadow-sm"><Eye size={16} /></a>}
                                            <button onClick={() => { setPayForm({ ...pay, amount: pay.received_amount, tds: pay.tds_deducted }); setActiveModal('record_payment'); }} className="p-2 bg-white rounded-lg text-slate-400 shadow-sm"><Edit3 size={16} /></button>
                                            <button onClick={() => deletePayment(pay.id)} className="p-2 bg-white rounded-lg text-rose-500 shadow-sm"><Trash2 size={16} /></button>
                                        </div>
                                    </div>
                                ))
                            }
                        </div>
                        <button onClick={() => { setPayForm({ id: null, invoice_id: selectedInvoice.id, amount: '', tds: 0, date: new Date().toISOString().split('T')[0], mode: 'NEFT', reference_no: '', notes: '', proof: null }); setActiveModal('record_payment'); }} className="w-full mt-10 py-5 bg-slate-900 text-white rounded-3xl font-black text-[10px] uppercase hover:bg-emerald-600 transition-all flex items-center justify-center gap-3 shadow-xl">
                            <Plus size={18} /> Add New Receipt
                        </button>
                    </div>
                </div>
            )}

            {/* PAYMENT MODAL */}
            {activeModal === 'record_payment' && (
                <div className="fixed inset-0 z-[250] flex items-center justify-center p-6 bg-slate-900/60 backdrop-blur-md">
                    <form onSubmit={handleRecordPayment} className="bg-white w-full max-w-xl rounded-[48px] shadow-2xl p-12 space-y-6">
                        <div className="flex justify-between items-center mb-4"><h2 className="text-2xl font-black italic tracking-tighter">{payForm.id ? 'Edit Receipt' : 'Record Receipt'}</h2><button type="button" onClick={() => setActiveModal(null)}><X /></button></div>
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-1"><label className="text-[9px] font-black text-slate-400 uppercase">Received</label><input required type="number" className="p-4 bg-slate-50 rounded-2xl font-bold w-full" value={payForm.amount} onChange={e => setPayForm({ ...payForm, amount: e.target.value })} /></div>
                            <div className="space-y-1"><label className="text-[9px] font-black text-slate-400 uppercase">TDS</label><input type="number" className="p-4 bg-slate-50 rounded-2xl font-bold w-full" value={payForm.tds} onChange={e => setPayForm({ ...payForm, tds: e.target.value })} /></div>
                        </div>
                        <input type="text" placeholder="Ref No (UTR/Check)" className="w-full bg-slate-50 p-4 rounded-2xl font-bold" value={payForm.reference_no} onChange={e => setPayForm({ ...payForm, reference_no: e.target.value })} />
                        <textarea placeholder="Payment Notes..." className="w-full bg-slate-50 p-4 rounded-2xl font-bold text-sm h-24 outline-none" value={payForm.notes} onChange={e => setPayForm({ ...payForm, notes: e.target.value })} />
                        <div className="relative border-2 border-dashed border-slate-200 rounded-2xl p-6 text-center hover:border-indigo-500 cursor-pointer">
                            <input type="file" className="absolute inset-0 opacity-0 cursor-pointer" onChange={e => setPayForm({ ...payForm, proof: e.target.files[0] })} />
                            <Upload className="mx-auto text-slate-300 mb-2" /><span className="text-xs font-bold text-slate-500">{payForm.proof ? (payForm.proof.name || 'File Selected') : 'Upload Receipt Screenshot'}</span>
                        </div>
                        <button className="w-full py-5 bg-indigo-600 text-white rounded-3xl font-black shadow-xl">{payForm.id ? 'Update Ledger' : 'Record Payment'}</button>
                    </form>
                </div>
            )}
            {toast && (
                <div className="fixed bottom-10 left-1/2 -translate-x-1/2 z-[500] animate-in slide-in-from-bottom duration-300">
                    <div className={`px-6 py-3 rounded-2xl shadow-2xl flex items-center gap-3 border font-black text-xs uppercase italic ${toast.type === 'success' ? 'bg-emerald-500 border-emerald-400 text-white' : 'bg-rose-500 border-rose-400 text-white'
                        }`}>
                        <CheckCircle size={18} /> {toast.msg}
                    </div>
                </div>
            )}
        </div>
    );
};

export default ClientBilling1;